import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useClearCustomerStatusOverride, useSetCustomerStatusOverride } from "@/hooks/use-customer-status";
import { CUSTOMER_STATUS_LABELS, type CustomerStatus } from "@/lib/customer-status";
import type { DBCustomerStatusRow } from "@/lib/db-types";

interface CustomerStatusOverrideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  /** Dagens status for kunden; styrer forhåndsvalg og om «Fjern overstyring» vises. */
  current: DBCustomerStatusRow | null;
}

/** Overstyr kundestatus med påkrevd begrunnelse og valgfri utløpsdato. */
export default function CustomerStatusOverrideDialog({
  open,
  onOpenChange,
  customerId,
  current,
}: CustomerStatusOverrideDialogProps) {
  const setOverride = useSetCustomerStatusOverride();
  const clearOverride = useClearCustomerStatusOverride();
  const [status, setStatus] = useState<CustomerStatus>("warm");
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState("");

  useEffect(() => {
    if (!open) return;
    setStatus(current?.is_overridden ? current.effective_status : current?.derived_status ?? "warm");
    setReason(current?.is_overridden ? current.override_reason ?? "" : "");
    setUntil(current?.is_overridden ? current.override_until ?? "" : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId]);

  const busy = setOverride.isPending || clearOverride.isPending;
  const canSave = reason.trim().length > 0 && !busy;

  const save = async () => {
    await setOverride.mutateAsync({ customerId, status, reason, until: until || null });
    onOpenChange(false);
  };
  const clear = async () => {
    await clearOverride.mutateAsync(customerId);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Overstyr kundestatus</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) save().catch(() => undefined);
          }}
        >
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="status-override-status">Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as CustomerStatus)}>
                <SelectTrigger id="status-override-status" aria-label="Velg status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(CUSTOMER_STATUS_LABELS) as CustomerStatus[]).map((s) => (
                    <SelectItem key={s} value={s}>
                      {CUSTOMER_STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="status-override-reason">Begrunnelse</Label>
              <Input
                id="status-override-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Hvorfor avviker statusen fra kjøpshistorikken?"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="status-override-until">Til dato (valgfritt)</Label>
              <Input
                id="status-override-until"
                type="date"
                value={until}
                onChange={(e) => setUntil(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Etter denne datoen gjelder den avledede statusen igjen.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            {current?.is_overridden ? (
              <Button type="button" variant="outline" onClick={() => clear().catch(() => undefined)} disabled={busy}>
                Fjern overstyring
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Avbryt
              </Button>
              <Button type="submit" disabled={!canSave} aria-label="Lagre overstyring">
                Lagre
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
